import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AlertTriangle, TrendingDown, Send, Loader2, Download, FileText } from "lucide-react";

const STATUS_STYLES = {
  red: "bg-[hsl(6_60%_92%)] text-[hsl(6_62%_45%)]",
  amber: "bg-[hsl(38_90%_88%)] text-[hsl(35_75%_38%)]",
  green: "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]",
  idle: "bg-[hsl(var(--rule))] text-[hsl(var(--ink-soft))]",
};

export default function InventoryReports({ locations, isDirector }) {
  const [tab, setTab] = useState("reorder");   // reorder | days | digest
  const [location, setLocation] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [digestBody, setDigestBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (tab === "digest") return;
    setLoading(true);
    try {
      const params = location ? { location } : {};
      const url = tab === "reorder"
        ? "/staff/inventory/v2/reports/reorder"
        : "/staff/inventory/v2/reports/days-of-cover";
      const { data } = await api.get(url, { params });
      setRows(data.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load report");
    } finally { setLoading(false); }
  }, [tab, location]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== "digest") return;
    (async () => {
      try {
        const { data } = await api.get("/staff/inventory/v2/reports/variance-digest/preview");
        setDigestBody(data.body);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load digest preview");
      }
    })();
  }, [tab]);

  const downloadXlsx = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${backendUrl}/api/staff/inventory/v2/reports/reorder.xlsx${location ? `?location=${location}` : ""}`;
    window.open(url, "_blank");
  };

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/staff/inventory/v2/reports/variance-digest/send");
      if (data.whatsapp?.ok) toast.success("Digest sent on WhatsApp");
      else if (data.whatsapp?.skipped) toast.warning("Twilio credentials not configured — digest generated only");
      else toast.warning(data.whatsapp?.error || "WhatsApp send failed — digest preview only");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="inventory-reports" className="space-y-4">
      <div className="flex gap-1 flex-wrap">
        <button data-testid="report-tab-reorder" onClick={() => setTab("reorder")}
                className={`px-4 py-2 text-xs uppercase tracking-wider rounded-full ${tab === "reorder" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]"}`}>
          <AlertTriangle size={12} className="inline mr-1" /> Reorder
        </button>
        <button data-testid="report-tab-days" onClick={() => setTab("days")}
                className={`px-4 py-2 text-xs uppercase tracking-wider rounded-full ${tab === "days" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]"}`}>
          <TrendingDown size={12} className="inline mr-1" /> Days of Cover
        </button>
        {isDirector && (
          <button data-testid="report-tab-digest" onClick={() => setTab("digest")}
                  className={`px-4 py-2 text-xs uppercase tracking-wider rounded-full ${tab === "digest" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))]"}`}>
            <FileText size={12} className="inline mr-1" /> Weekly Digest
          </button>
        )}
      </div>

      {tab !== "digest" && (
        <div className="flex flex-wrap gap-2 items-center">
          <select data-testid="report-location" value={location} onChange={e => setLocation(e.target.value)} className="tbb-inp text-sm">
            <option value="">All locations</option>
            {locations.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          {tab === "reorder" && (
            <button data-testid="report-download-xlsx" onClick={downloadXlsx} className="text-xs px-3 py-1.5 border border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))] flex items-center gap-1">
              <Download size={12} /> Excel
            </button>
          )}
          <span className="text-xs text-[hsl(var(--ink-soft))] ml-auto">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
        </div>
      )}

      {loading && <div className="p-6 text-center text-[hsl(var(--ink-soft))]"><Loader2 size={20} className="animate-spin inline" /></div>}

      {!loading && tab === "reorder" && (
        <div className="border border-[hsl(var(--rule))] divide-y divide-[hsl(var(--rule))]">
          {rows.length === 0 && <div className="p-6 text-center text-[hsl(var(--ink-soft))] text-sm">Everything is above reorder point 🎉</div>}
          {rows.map(r => (
            <div key={`${r.sku}-${r.location}`} data-testid={`reorder-row-${r.sku}`} className="p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{r.name}</div>
                  <div className="text-xs text-[hsl(var(--ink-soft))]">
                    {r.sku} · {r.location} · on hand <b className="text-[hsl(6_62%_45%)]">{r.on_hand} {r.base_uom}</b> / reorder {r.reorder_point} · par {r.par_level}
                  </div>
                  {r.default_vendor && <div className="text-xs text-[hsl(var(--brass-2))] mt-0.5">Vendor: {r.default_vendor}</div>}
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold">{r.suggested_order_qty}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">suggested</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && tab === "days" && (
        <div className="border border-[hsl(var(--rule))] divide-y divide-[hsl(var(--rule))] max-h-[70vh] overflow-y-auto">
          {rows.length === 0 && <div className="p-6 text-center text-[hsl(var(--ink-soft))] text-sm">No consumption data yet.</div>}
          {rows.map(r => (
            <div key={`${r.sku}-${r.location}`} data-testid={`days-row-${r.sku}`} className="p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm truncate">{r.name}</div>
                <div className="text-xs text-[hsl(var(--ink-soft))]">
                  {r.sku} · {r.location} · on hand {r.on_hand} · avg {r.avg_daily_issue}/day
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold">{r.days_of_cover ?? "∞"}</div>
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_STYLES[r.status]}`}>{r.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "digest" && (
        <div className="space-y-3 max-w-2xl">
          <div className="p-4 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] whitespace-pre-wrap font-mono text-xs text-[hsl(var(--ink))]">
            {digestBody || "(loading…)"}
          </div>
          <button data-testid="digest-send" onClick={sendDigest} disabled={sending || !digestBody} className="btn-brass !py-2.5 !px-5 text-sm disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin inline" /> : <><Send size={14} className="inline mr-1" /> Send to Director on WhatsApp</>}
          </button>
          <div className="text-xs text-[hsl(var(--ink-soft))]">
            Auto-sent every Monday at 08:00 IST. The button above lets you resend on demand.
          </div>
        </div>
      )}

      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
